import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useUserRole, AppRole } from "./useUserRole";

export type PermissionEntity =
  | "assets"
  | "deficiencies"
  | "find"
  | "activity_log"
  | "future_enhancements"
  | "users"
  | "auth_log"
  | "permissions";

export type PermissionAction =
  | "create"
  | "read"
  | "update"
  | "delete"
  | "export"
  | "bulk"
  | "view_archived";

interface RolePermission {
  role: AppRole;
  entity: string;
  can_create: boolean;
  can_read: boolean;
  can_update: boolean;
  can_delete: boolean;
  can_export: boolean;
  can_bulk: boolean;
  can_view_archived: boolean;
  visible: boolean;
  landing_page: string | null;
  nav_order: number;
  more_position: number;
}

export interface NavItem {
  entity: string;
  path: string;
  label: string;
  nav_order: number;
}

const ENTITY_NAV_MAP: Record<string, { path: string; label: string }> = {
  find: { path: "/issues", label: "Find" },
  deficiencies: { path: "/management", label: "Issues" },
  assets: { path: "/assets", label: "Assets" },
  activity_log: { path: "/activity", label: "Activity" },
  users: { path: "/roles", label: "Users" },
  future_enhancements: { path: "/future", label: "Future" },
  auth_log: { path: "/auth-log", label: "Auth Log" },
  permissions: { path: "/permissions", label: "Permissions" },
};

export const usePermissions = () => {
  const { user } = useAuth();
  const { roles, loading: rolesLoading } = useUserRole();
  const [permissions, setPermissions] = useState<RolePermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const handler = () => setRefreshKey((k) => k + 1);
    window.addEventListener("permissions-changed", handler);
    return () => window.removeEventListener("permissions-changed", handler);
  }, []);

  useEffect(() => {
    if (!user) {
      setPermissions([]);
      setLoading(false);
      return;
    }

    if (rolesLoading) {
      setLoading(true);
      return;
    }

    if (roles.length === 0) {
      setPermissions([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const fetchPermissions = async () => {
      const { data, error } = await supabase
        .from("role_permissions")
        .select("*")
        .in("role", roles)
        .order("nav_order", { ascending: true });

      if (error) {
        setPermissions([]);
      } else if (data) {
        setPermissions(data as unknown as RolePermission[]);
      }

      setLoading(false);
    };

    fetchPermissions();
  }, [user, roles, rolesLoading, refreshKey]);

  const can = useCallback(
    (action: PermissionAction, entity: PermissionEntity): boolean => {
      if (loading) return false;
      const key = `can_${action}` as keyof RolePermission;
      return permissions
        .filter((p) => p.entity === entity)
        .some((p) => p[key] === true);
    },
    [permissions, loading]
  );

  const isVisible = useCallback(
    (entity: PermissionEntity): boolean => {
      if (loading) return false;
      return permissions
        .filter((p) => p.entity === entity)
        .some((p) => p.visible === true);
    },
    [permissions, loading]
  );

  // Ordered visible nav items — merges across roles, picks lowest nav_order, sorted
  const orderedNavItems = useMemo((): NavItem[] => {
    if (loading) return [];
    const entityMap = new Map<string, number>();
    for (const p of permissions) {
      if (!p.visible) continue;
      const mapping = ENTITY_NAV_MAP[p.entity];
      if (!mapping) continue;
      const existing = entityMap.get(p.entity);
      if (existing === undefined || p.nav_order < existing) {
        entityMap.set(p.entity, p.nav_order);
      }
    }
    return Array.from(entityMap.entries())
      .map(([entity, nav_order]) => ({
        entity,
        ...ENTITY_NAV_MAP[entity],
        nav_order,
      }))
      .sort((a, b) => a.nav_order - b.nav_order);
  }, [permissions, loading]);

  // More position — use the lowest more_position across all user roles
  const morePosition = useMemo(() => {
    if (loading || permissions.length === 0) return 3;
    const positions = permissions.map((p) => p.more_position).filter((v) => v > 0);
    return positions.length > 0 ? Math.min(...positions) : 3;
  }, [permissions, loading]);

  const landingPage = useMemo(() => {
    if (loading) return "/";

    // Use the landing_page field from permissions (pick the first non-null across roles)
    const dbLanding = permissions
      .filter((p) => p.landing_page)
      .sort((a, b) => a.nav_order - b.nav_order)[0]?.landing_page;

    if (dbLanding) {
      // landing_page stores an entity name — map it to a path
      const mapped = ENTITY_NAV_MAP[dbLanding]?.path;
      if (mapped) return mapped;
      // If it's already a path (starts with /), use directly
      if (dbLanding.startsWith("/")) return dbLanding;
    }

    // Fallback: first visible nav item
    return orderedNavItems[0]?.path || "/issues";
  }, [permissions, orderedNavItems, loading]);

  return { can, isVisible, landingPage, orderedNavItems, morePosition, permissions, loading: loading || rolesLoading };
};
